const io = require('socket.io-client');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');

/**
 * Initializes and authenticates a WebSocket connection to the DigiTickets 'socket' server.
 * Used for kitchen and kitchen-customer screens to receive instant updates pushed from the app server via the socket.
 *
 * Note that this is nothing to do with the ProPoint Agent / Verifone / WorldPay WebSockets or the Payyo websockets.
 * This is about the connection from the kitchen screen to the hosted "DigiTickets Socket Server".
 */
class WebsocketService {
    /**
     * @param {AppConfig} appConfig
     * @param {EventBus} eventBus
     * @param {ToastFactory} toastFactory
     * @param {UserService} userService
     * @param {VuexService} vuexService
     */
    constructor(
        appConfig,
        eventBus,
        toastFactory,
        userService,
        vuexService,
    ) {
        this.eventBus = eventBus;
        this.toastFactory = toastFactory;
        this.userService = userService;
        this.vuexService = vuexService;

        this.socketUrl = appConfig.socketUrl;

        /**
         * Socket.io connection.
         *
         * @type {socket.io-client.Socket|null}
         */
        this.ioSocket = null;

        this.log = logFactory('WebsocketService');
    }

    connect() {
        this.ioSocket = io(this.socketUrl);

        this.ioSocket.on('disconnect', () => {
            this.log.debug('Websocket disconnected');
            this.vuexService.getStore().commit('websocket/setConnected', false);
            this.eventBus.broadcast('websocket:disconnect');
        });

        this.ioSocket.on('connect', () => {
            this.log.debug('Websocket connected');
            this.vuexService.getStore().commit('websocket/setConnected', true);
            this.eventBus.broadcast('websocket:connect');
            this.authenticate();
        });

        this.ioSocket.on('message', (data) => {
            this.log.debug('Websocket message', data);
            this.eventBus.broadcast('websocket:message', data);
        });
    }

    /**
     * @return {socket.io-client.Socket}
     */
    getSocket() {
        return this.ioSocket;
    }

    authenticate() {
        if (!this.userService.isLoggedIn()) {
            return;
        }

        const previousBranchID = this.vuexService.getStore().state.websocket.authenticatedBranchID;

        if (this.userService.currentBranch?.ID !== previousBranchID) {
            // Changing branch
            this.vuexService.getStore().commit('websocket/setAuthenticatedBranchID', false);
        }

        if (this.userService.currentBranch) {
            this.sendAuthenticateMessage(this.userService.getApiKey(), this.userService.currentBranch.ID)
                .then(
                    (response) => {
                        this.log.debug('Websocket authenticated', response);

                        const branchID = response.branch?.ID;
                        this.vuexService.getStore().commit('websocket/setAuthenticatedBranchID', branchID);

                        if (branchID !== previousBranchID) {
                            this.eventBus.broadcast('websocket:authenticated', response);
                        }
                    },
                    (response) => {
                        this.log.error('Websocket authentication failed', response);

                        this.vuexService.getStore().commit('websocket/setAuthenticatedBranchID', null);

                        if (response.error) {
                            this.toastFactory.warningTop(null, response.error);
                        }
                    },
                );
        }
    }

    /**
     * @private
     *
     * @param {string} apiKey
     * @param {number} branchID
     *
     * @return {Promise}
     */
    sendAuthenticateMessage(apiKey, branchID) {
        return new Promise((resolve, reject) => {
            this.log.debug('Sending websocket authenticate message', apiKey, branchID);
            this.getSocket().emit(
                'authenticate',
                {
                    apiKey,
                    branchID,
                },
                (response) => {
                    if (response.success) {
                        resolve(response);
                    } else {
                        reject(response);
                    }
                },
            );
        });
    }

    /**
     * @param {string} type
     * @param {object} data
     *
     * @return {Promise}
     */
    sendMessage(type, data) {
        return new Promise((resolve, reject) => {
            this.log.debug('Sending websocket message', type, data);
            this.getSocket().emit(
                'message',
                {
                    type,
                    data,
                },
                (response) => {
                    if (response.success) {
                        resolve(response);
                    } else {
                        reject(response);
                    }
                },
            );
        });
    }
}

module.exports = WebsocketService;
